import React from 'react';
import { Box, Container, Typography, styled, Chip } from '@mui/material';
import Grid from '@mui/material/Grid'; 
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Icons
import AndroidIcon from '@mui/icons-material/Android';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import LanguageIcon from '@mui/icons-material/Language';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BrushIcon from '@mui/icons-material/Brush';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const SectionWrapper = styled(Box)({
  padding: '100px 0',
  background: '#030712',
  position: 'relative',
  overflow: 'hidden',
});

const ServiceCard = styled(motion.div)(({ bordercolor }) => ({
  padding: '36px',
  height: '100%',
  width: '100%',
  background: 'rgba(15, 23, 42, 0.5)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(12px)',
  cursor: 'pointer',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-10%',
    left: '-10%',
    width: '140px',
    height: '140px',
    background: bordercolor,
    filter: 'blur(70px)',
    opacity: 0.12,
    transition: 'all 0.5s ease',
  },

  '&:hover': {
    border: `1px solid ${bordercolor}88`,
    background: 'rgba(15, 23, 42, 0.85)',
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 40px -15px ${bordercolor}33`,
    '&::before': {
      opacity: 0.35,
      transform: 'scale(1.4)',
    },
    '& .cta-link': {
      color: bordercolor,
      transform: 'translateX(6px)',
    },
    '& .cta-icon': {
      transform: 'translateX(4px)',
    }
  }
}));

const IconContainer = styled(Box)(({ color }) => ({
  marginBottom: '20px',
  display: 'inline-flex',
  padding: '12px',
  borderRadius: '16px',
  background: `${color}15`,
  border: `1px solid ${color}30`,
  width: 'fit-content',
  '& svg': {
    fontSize: '2.2rem',
    color: color,
    filter: `drop-shadow(0 0 10px ${color}55)`,
  }
}));

const AllServicesGrid = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "android-dev",
      title: "Native Android Development",
      route: "/services/android-development",
      desc: "Mission-critical native Android applications engineered with Kotlin, Jetpack Compose, and Clean Architecture (MVVM/MVI). Deep integration expertise in Zebra SDKs, RFID/NFC hardware scanning, offline Room DB sync, and Play Store publishing.",
      icon: <AndroidIcon />,
      color: "#2dd4bf", 
      tags: ["Kotlin", "Jetpack Compose", "Clean Architecture", "Hardware SDKs"]
    },
    {
      id: "hybrid-dev",
      title: "Hybrid App Development",
      route: "/services/hybrid-development",
      desc: "Cross-platform mobile applications built using Flutter and React Native. High code-reusability paired with 60 FPS performance, custom native bridges, state management (Riverpod/Redux), and unified iOS and Android deployments.",
      icon: <PhonelinkIcon />,
      color: "#3b82f6", 
      tags: ["Flutter", "React Native", "iOS & Android", "Native Modules"]
    },
    {
      id: "web-dev",
      title: "Website & Web App Development",
      route: "/services/website-development",
      desc: "Scalable web ecosystems using Next.js, React, TypeScript, and Tailwind CSS. Built for maximum speed with SSR/SSG, real-time backend integrations, SEO optimization, and responsive design systems.",
      icon: <LanguageIcon />,
      color: "#8b5cf6", 
      tags: ["Next.js", "React", "TypeScript", "Tailwind CSS"]
    },
    {
      id: "it-consultation",
      title: "IT Consultation & Architecture",
      route: "/services/it-consultation",
      desc: "Strategic technical roadmaps, tech stack evaluation, and system architecture design. Helping teams optimize legacy codebases, enforce CI/CD pipelines, reduce technical debt, and scale infrastructure securely.",
      icon: <EngineeringIcon />,
      color: "#f59e0b", 
      tags: ["System Design", "Tech Stack Audit", "CI/CD Pipelines", "Code Review"]
    },
    {
      id: "ui-ux-design",
      title: "UI/UX Design & Prototyping",
      route: "/services/ui-ux-design",
      desc: "User-centric design systems, wireframing, and high-fidelity Figma prototypes. Crafting accessible, conversion-driven visual experiences tailored for web, tablet, and mobile platforms.",
      icon: <BrushIcon />,
      color: "#ec4899", 
      tags: ["Figma", "Design Systems", "Prototyping", "User Research"]
    },
    {
      id: "cloud-devops",
      title: "Cloud Services & DevOps",
      route: "/services/cloud-devops",
      desc: "Cloud-native infrastructure management using AWS, Firebase, and Docker. Implementing automated build pipelines, microservices architecture, serverless APIs, and zero-downtime deployments.",
      icon: <CloudQueueIcon />,
      color: "#06b6d4", 
      tags: ["AWS", "Firebase", "Docker", "REST & GraphQL"]
    }
  ];

  return (
    <SectionWrapper id="services">
      <Container maxWidth="lg">
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography 
            variant="overline" 
            sx={{ 
              color: '#3b82f6', 
              fontWeight: 900, 
              letterSpacing: 3, 
              fontSize: '0.8rem',
              display: 'block',
              mb: 1
            }}
          >
            PRECISION ENGINEERING
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              color: '#f8fafc', 
              fontSize: { xs: '2.2rem', md: '3.2rem' }, 
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}
          >
            Innovate. <span style={{ color: '#3b82f6' }}>Scale.</span> Deliver.
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              color: '#94a3b8', 
              mt: 2.5, 
              maxWidth: '640px', 
              mx: 'auto', 
              lineHeight: 1.7, 
              fontSize: '1.05rem' 
            }}
          >
            End-to-end digital engineering services tailored for scalability, hardware integration, and seamless user experiences.
          </Typography>
        </Box>

        <Grid container spacing={3.5} columns={12}>
          {services.map((service, index) => (
            <Grid key={service.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <ServiceCard
                bordercolor={service.color}
                onClick={() => navigate(service.route)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(service.route);
                  }
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <IconContainer color={service.color}>
                  {service.icon}
                </IconContainer>
                
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#f8fafc', 
                    fontWeight: 700, 
                    mb: 1.5, 
                    fontSize: '1.3rem',
                    lineHeight: 1.3
                  }}
                >
                  {service.title}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#94a3b8', 
                    lineHeight: 1.65, 
                    fontSize: '0.925rem', 
                    mb: 3,
                    flexGrow: 1
                  }}
                >
                  {service.desc}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
                  {service.tags.map((tag, tagIdx) => (
                    <Chip
                      key={tagIdx}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        color: '#cbd5e1',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        fontSize: '0.725rem',
                        fontWeight: 500,
                        height: '24px'
                      }}
                    />
                  ))}
                </Box>

                <Box 
                  className="cta-link"
                  sx={{ 
                    mt: 'auto', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    color: '#94a3b8', 
                    fontWeight: 700, 
                    fontSize: '0.85rem', 
                    letterSpacing: 0.5,
                    transition: 'all 0.3s ease'
                  }}
                >
                  EXPLORE SERVICE 
                  <ArrowForwardIcon className="cta-icon" sx={{ fontSize: '1rem', transition: 'transform 0.3s ease' }} />
                </Box>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionWrapper>
  );
};

export default AllServicesGrid;